import { axialHexDistance } from '../../../hexmap/coords';
import { getHexTileMapKey } from '../../../hexmap/model/keys';
import type { HexTileMapKey, HexTileType } from '../../../hexmap/model/types';
import { type Reachable, reachableFrom } from '../../../hexmap/pathfinding';
import type { TickDerived } from '../../derived';
import { exploredLookup } from '../../explored';
import { buildingCells } from '../../footprint';
import { cityRadius } from '../../levels';
import { holdsStock, nearestStockCity } from '../../stock';
import { mintOrder } from '../../territory';
import type { Building, Cell, EntityId, PlayerId, SimContext, SimState, Unit } from '../../types';

/**
 * Shared state for one automation pass (see index.ts): per-tick and per-owner
 * caches (vision, territory, blockers, searches, claims) and the small helpers
 * scouts and workers both use.
 */

const RETRY_TICKS = 10;
/**
 * Most full work searches (`stepWorker` looking for new work) one owner's
 * workers start in a tick; the rest wait a tick, so a crowd needing work
 * at once (a city's workers after a raid, the first tick) spreads its
 * searches over the next ticks instead of spiking one.
 */
const MAX_WORK_SEARCHES = 8;
/** Worker searches reach this many hexes past their home city's territory. */
const WORK_MARGIN = 3;

/**
 * The first tick at least `wait` ticks from now in `unit`'s own phase of
 * `wait` (by its id), so units that fail a search together (e.g. all made on
 * the same tick) retry spread over the next `wait` ticks rather than all on
 * one tick. Deterministic: from the tick and id only.
 */
function retryAt(state: SimState, unit: Unit, wait: number): number {
  const at = state.tick + wait;
  const phase = (((mintOrder(unit.id) || 0) % wait) + wait) % wait;
  return at + ((((phase - at) % wait) + wait) % wait);
}

interface OwnerCache {
  explored: (key: HexTileMapKey) => boolean;
  /** Tile keys of this owner's territory. */
  territory: Set<HexTileMapKey> | null;
  /** Tile → workers gathering there, heading there, or hauling from there. */
  claims: Map<HexTileMapKey, EntityId[]> | null;
  /** Home city id → resource → its auto workers holding gathering work on it. */
  workerCounts: Map<EntityId, Map<string, number>> | null;
  /** Auto worker id → the home city and resource it's counted under in `workerCounts`. */
  counted: Map<EntityId, { home: EntityId; res: string }> | null;
  /** City id → task → the workers on it there: own pinned and auto, and visiting (see worker.ts `countTask`). */
  taskCounts: Map<EntityId, Map<string, { pinned: number; auto: number; visiting: number }>> | null;
  /** Worker id → the city, task and kind it's counted under in `taskCounts`. */
  tasked: Map<
    EntityId,
    { city: EntityId; task: string; kind: 'pinned' | 'auto' | 'visiting' }
  > | null;
  /** `${terrain}@${city}` → a search from that city (walking distances for `throughput`). */
  cityReaches: Map<string, Reachable> | null;
  /** Scout id → the frontier it's heading for. */
  scoutTargets: Map<EntityId, Cell> | null;
  /** Scout id → where it stands. */
  scoutPositions: Map<EntityId, Cell> | null;
  /** Tiles scouts keep out of (see `scoutDanger`). */
  danger: Set<HexTileMapKey> | null;
  /** Tiles of rival territory this owner sees right now. */
  rivalTerritory: Set<HexTileMapKey> | null;
  /** `${radius}:${tile}` → unexplored map tiles within radius. */
  unexplored: Map<string, Cell[]> | null;
  /** Positions of enemy fighters this owner can currently see. */
  threats: Cell[] | null;
  /** Tile keys of this owner's territory that shelters them (garrison cities). */
  shelters: Set<HexTileMapKey> | null;
  /** The tick's shared layer: vision, blockers (see derived.ts). */
  derived: TickDerived;
  /**
   * `${terrain}@${tile}` → `reachFor`'s search from there: units of one type
   * on one tile (workers leaving a city) share it, which matters on a big map.
   */
  reaches: Map<string, Reachable> | null;
  /** `${city}:${task}` a full-reach work search found no place for this tick (see `stepWorker`). */
  noPlace: Set<string> | null;
  /** Tile → whether it's a frontier (see `isFrontier`). */
  frontier: Map<HexTileMapKey, boolean> | null;
  /** By tile index of the map's grid (pathfinding.ts `gridOf`): 1 for an unexplored tile. */
  unexploredBits: Uint8Array | null;
}

interface TickCache {
  /**
   * The tick's shared layer (derived.ts): vision and territory, and the
   * occupancy and blockers of this pass (automation updates its occupancy
   * as it sends units off).
   */
  derived: TickDerived;
  owners: Map<PlayerId, OwnerCache>;
  buildingsAt: Map<HexTileMapKey, Building[]> | null;
  /** City id → its territory's tiles. */
  cityTiles: Map<EntityId, Cell[]> | null;
  /** Owner → full work searches started this tick (see MAX_WORK_SEARCHES). */
  searches: Map<PlayerId, number>;
}

/** Whether `owner` may start another full work search this tick; counts it if so. */
function takeSearch(cache: TickCache, owner: PlayerId): boolean {
  const n = cache.searches.get(owner) ?? 0;
  if (n >= MAX_WORK_SEARCHES) return false;
  cache.searches.set(owner, n + 1);
  return true;
}

/**
 * The area a worker of `city` searches (`reachableFrom`'s `within`): the
 * disc of its territory's radius plus WORK_MARGIN, so work in the territory
 * is still reached by paths that bend a little outside it.
 */
function workRadius(ctx: SimContext, city: Building): number {
  return (cityRadius(ctx.rules, city) ?? 0) + WORK_MARGIN;
}

function workArea(ctx: SimContext, city: Building): (q: number, r: number) => boolean {
  const radius = workRadius(ctx, city);
  const { q: cq, r: cr } = city.pos;
  // Axial distance inline: called for every tile the search looks at.
  return (q, r) => {
    const dq = q - cq;
    const dr = r - cr;
    return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2 <= radius;
  };
}

/** Whether `cell` lies in `city`'s `workArea`. */
function inWorkArea(ctx: SimContext, city: Building, cell: Cell): boolean {
  return axialHexDistance(cell, city.pos) <= workRadius(ctx, city);
}

function ownerCache(state: SimState, ctx: SimContext, cache: TickCache, owner: PlayerId) {
  let entry = cache.owners.get(owner);
  if (!entry) {
    entry = {
      explored: exploredLookup(ctx.terrain, state.explored[owner]),
      territory: null,
      claims: null,
      workerCounts: null,
      counted: null,
      taskCounts: null,
      tasked: null,
      cityReaches: null,
      scoutTargets: null,
      scoutPositions: null,
      danger: null,
      rivalTerritory: null,
      unexplored: null,
      threats: null,
      shelters: null,
      derived: cache.derived,
      reaches: null,
      noPlace: null,
      frontier: null,
      unexploredBits: null,
    };
    cache.owners.set(owner, entry);
  }
  return entry;
}

/**
 * Where `unit` can walk: its terrain, around the blockers its owner can see
 * (fog-safe; movement.ts waits and re-routes at hidden ones). With `area`,
 * only within that city's `workArea` (the caller checks the unit is in it).
 */
function reachFor(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  terrain: readonly HexTileType[],
  area?: Building
) {
  const oc = ownerCache(state, ctx, cache, unit.owner);
  // Same start, terrain, blockers and area give the same search (read-only to callers).
  oc.reaches ??= new Map();
  const memo = `${terrain.join('|')}@${getHexTileMapKey(unit.pos)}${area ? `~${area.id}` : ''}`;
  let reach = oc.reaches.get(memo);
  if (!reach) {
    reach = reachableFrom(
      ctx.terrain,
      unit.pos,
      terrain,
      oc.derived.blockers(unit.owner),
      undefined,
      area && workArea(ctx, area)
    );
    oc.reaches.set(memo, reach);
  }
  return reach;
}

/** Tile → buildings covering it (every tile of a footprint). */
function buildingsAt(state: SimState, cache: TickCache): Map<HexTileMapKey, Building[]> {
  if (!cache.buildingsAt) {
    cache.buildingsAt = new Map();
    for (const b of Object.values(state.buildings)) {
      for (const cell of buildingCells(b)) {
        const key = getHexTileMapKey(cell);
        const list = cache.buildingsAt.get(key);
        if (list) list.push(b);
        else cache.buildingsAt.set(key, [b]);
      }
    }
  }
  return cache.buildingsAt;
}

function isPos(b: Building, cell: Cell): boolean {
  return b.pos.q === cell.q && b.pos.r === cell.r;
}

/** The owner's territory and shelter tiles, from the tick's `territoryOwners`. */
function ownTerritory(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  owner: PlayerId,
  oc: OwnerCache
): { territory: Set<HexTileMapKey>; shelters: Set<HexTileMapKey> } {
  if (!oc.territory || !oc.shelters) {
    const territory = new Set<HexTileMapKey>();
    const shelters = new Set<HexTileMapKey>();
    for (const [key, cityId] of cache.derived.owners()) {
      const city = state.buildings[cityId];
      if (city?.owner !== owner) continue;
      territory.add(key);
      if (ctx.rules.buildings[city.type]?.garrison) shelters.add(key);
    }
    oc.territory = territory;
    oc.shelters = shelters;
  }
  return { territory: oc.territory, shelters: oc.shelters };
}

/**
 * A search from city `city` over `terrain` (walking distances to it), once
 * per tick, within its `workArea`: what its workers' `throughput` needs.
 */
function cityReach(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  city: Building,
  terrain: readonly HexTileType[]
): Reachable {
  const oc = ownerCache(state, ctx, cache, city.owner);
  oc.cityReaches ??= new Map();
  const memo = `${terrain.join('|')}@${city.id}`;
  let reach = oc.cityReaches.get(memo);
  if (!reach) {
    reach = reachableFrom(
      ctx.terrain,
      city.pos,
      terrain,
      oc.derived.blockers(city.owner),
      undefined,
      workArea(ctx, city)
    );
    oc.cityReaches.set(memo, reach);
  }
  return reach;
}

function isMoving(unit: Unit): boolean {
  return unit.path !== undefined && unit.path.length > 0;
}

/**
 * The worker's home city: its `homeCityId` while that's an own completed
 * city, else (never set, or the city fell) the nearest one, which becomes
 * its home. Undefined when the owner has no completed city.
 */
function homeCity(state: SimState, ctx: SimContext, unit: Unit): Building | undefined {
  const home = state.buildings[unit.homeCityId ?? ''];
  if (home && home.owner === unit.owner && holdsStock(ctx.rules, home)) return home;
  const nearest = nearestStockCity(state, ctx.rules, unit.owner, unit.pos);
  if (nearest) unit.homeCityId = nearest.id;
  else delete unit.homeCityId;
  return nearest;
}

/** Tiles of city `cityId`'s territory, from the tick's `territoryOwners`. */
function cityTiles(cache: TickCache, cityId: EntityId): Cell[] {
  if (!cache.cityTiles) {
    cache.cityTiles = new Map();
    for (const [key, id] of cache.derived.owners()) {
      const [q, r] = key.split(',').map(Number);
      const list = cache.cityTiles.get(id);
      if (list) list.push({ q, r });
      else cache.cityTiles.set(id, [{ q, r }]);
    }
  }
  return cache.cityTiles.get(cityId) ?? [];
}

export {
  buildingsAt,
  cityReach,
  cityTiles,
  homeCity,
  inWorkArea,
  isMoving,
  isPos,
  MAX_WORK_SEARCHES,
  type OwnerCache,
  ownerCache,
  ownTerritory,
  RETRY_TICKS,
  reachFor,
  retryAt,
  type TickCache,
  takeSearch,
  WORK_MARGIN,
};
